import { useQuery } from "@tanstack/react-query"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useState } from "react"

import {
  Button,
  cn,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client } from "#utils/graphql-client.ts"

const OperatorsByOrganizationDocument = graphql(/* GraphQL */ `
  query OperatorByOrganization($input: QueryOperatorByOrganizationInput!) {
    operatorByOrganization(input: $input) {
      id
      source
      name
    }
  }
`)

interface Operator {
  id: string
  source: AircraftSource
  name: string
}

interface FormOperatorSelectProps {
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string
  onOperatorSelect?: (operator: Operator) => void
}

export const FormOperatorSelect = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Select operator",
  disabled,
  className,
  error,
  onOperatorSelect,
}: FormOperatorSelectProps) => {
  const [open, setOpen] = useState(false)
  const {
    me: { organization },
  } = useLoggedInUserInfo()

  const { data } = useQuery({
    queryKey: queryKeys.operators.select(organization.id),
    queryFn: () =>
      client.request(OperatorsByOrganizationDocument, {
        input: { organizationId: organization.id, sources: ["LEON", "MANUAL"] },
      }),
  })

  const operators = data?.operatorByOrganization ?? []
  const selected = operators.find((o) => o.id === value) ?? null

  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            aria-invalid={error ? true : undefined}
            className={cn(
              "border-input hover:bg-input/10 h-10 w-full justify-between gap-2 truncate px-3 text-left font-normal",
              !value && "text-muted-foreground",
              error && "border-destructive dark:border-destructive",
            )}
          >
            <span className="truncate">
              {selected ? (
                selected.name
              ) : (
                <span className="text-placeholder">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="border-input w-(--radix-popper-anchor-width) p-1"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search operator..." />
            <CommandList>
              {operators.length > 0 && (
                <CommandGroup>
                  {operators.map((operator) => (
                    <CommandItem
                      key={operator.id}
                      value={operator.name}
                      onSelect={() => {
                        onChange(operator.id)
                        onOperatorSelect?.(operator)
                        setOpen(false)
                      }}
                    >
                      <span className="flex-1">{operator.name}</span>
                      {value === operator.id && (
                        <CheckIcon size={16} className="ml-auto" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
